import React, { useState, useEffect } from "react";
import { Users } from 'lucide-react';
import axios from "../../../../../axiosInstance.js";

/* -------------------- Reusable Wrapper -------------------- */
const InputWrapper = ({ label, required, error, children }) => (
  <div className="flex flex-col" style={{}}>
    <label className="mb-1 text-sm font-semibold text-gray-700"  style={{display: 'inline-block'}}>
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
    {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
  </div>
);

/* -------------------- Main Component -------------------- */
const ClientDetailsStep = ({ onNext, formData, setFormData }) => {
    const [loadingCustomerDetails, setLoadingCustomerDetails] = useState(false);
    const [errors, setErrors] = useState({});
    const [siteManagerName, setSiteManagerName] = useState("");
    const [customerPhone, setCustomerPhone] = useState("");

  /* ---------- Sync customerPhone with formData ---------- */
  useEffect(() => {
    if (formData.clientDetails?.customerPhone) {
      setCustomerPhone(formData.clientDetails.customerPhone);
    }
  }, [formData.clientDetails?.customerPhone]);

  /* ---------- Fetch Customer Details by Phone ---------- */
  const fetchCustomerDetailsByPhone = async (phone) => {
    if (phone.length !== 10) return;

    try {
      setLoadingCustomerDetails(true);
      
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/get-customer-by-phone/${phone}`
      );
      
      if (res.data?.status && res.data.data) {
        const customerData = res.data.data;
        setFormData((prev) => ({
          ...prev,
          clientDetails: {
            ...prev.clientDetails,
            customerPhone: phone,
            customerName: customerData.CustomerName || '',
            address: customerData.address || '',
            city: customerData.district || '',
            state: customerData.state || ''
          }
        }));
      }
    } catch (err) {
      console.error("Customer fetch error", err);
    } finally {
      setLoadingCustomerDetails(false);
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/[^\d]/g, '').slice(0, 10);
    setCustomerPhone(value);
    
    if (errors.customerPhone) {
      setErrors((prev) => ({ ...prev, customerPhone: "" }));
    }

    // Auto-fetch when 10 digits are entered
    if (value.length === 10) {
      fetchCustomerDetailsByPhone(value);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ 
      ...prev, 
      clientDetails: {
        ...prev.clientDetails,
        [name]: value
      }
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!customerPhone) newErrors.customerPhone = "Customer phone is required";
    else if (customerPhone.length !== 10) newErrors.customerPhone = "Phone must be 10 digits";
    if (!formData.clientDetails?.customerName) newErrors.customerName = "Customer name is required";
    if (!formData.clientDetails?.address) newErrors.address = "Address is required";
    if (!formData.clientDetails?.city) newErrors.city = "City is required";
    if (!formData.clientDetails?.state) newErrors.state = "State is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    console.log("Validating Client Details...");
    if (validateForm()) {
      console.log("Client Details Saved:", formData.clientDetails);
      onNext();
    }
  };

  const handleReset = () => {
    setCustomerPhone("");
    setFormData((prev) => ({
      ...prev,
      clientDetails: {
        customerName: "",
        address: "",
        city: "",
        state: "",
        siteManager: ""
      }
    }));
    setErrors({});
  };

  /* -------------------- UI -------------------- */
  return (
    <div className="space-y-5 pb-24">
      {/* Heading */}
      <div className="pb-3 pl-2 mb-4 border-b border-gray-200 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
          <Users size={20} className="text-blue-600" />
          Client Details
        </h3>
      </div>

      <form className="space-y-5">
        {/* Customer Phone Number */}
        <InputWrapper
          label="Customer Phone Number"
          required
          error={errors.customerPhone}
        >
          <div className="relative">
            <div className="flex items-center gap-3 pb-3">
              <div className="w-5 h-5 text-blue-500 flex-shrink-0">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.11-.27 11.36 11.36 0 003.48.56 1 1 0 011 1V20a1 1 0 01-1 1A17 17 0 013 4a1 1 0 011-1h3.5a1 1 0 011 1 11.36 11.36 0 00.56 3.48 1 1 0 01-.27 1.11l-2.17 2.2z"/>
                </svg>
              </div>
              <input
                type="tel"
                name="customerPhone"
                value={customerPhone}
                onChange={handlePhoneChange}
                placeholder="Enter 10-digit phone number"
                maxLength="10"
                className="flex-1 bg-transparent border-none outline-none text-gray-700 font-medium placeholder-gray-400 text-base"
              />
              {loadingCustomerDetails && (
                <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              )}
            </div>
            <div className="h-0.5 bg-gradient-to-r from-gray-400 to-gray-300 rounded-full"></div>
          </div>
        </InputWrapper>

        {/* Customer Name */}
        <InputWrapper
          label="Customer Name"
          required
          error={errors.customerName}
        >
          <div className="relative">
            <div className="flex items-center gap-3 pb-3">
              <div className="w-5 h-5 text-blue-500 flex-shrink-0">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                </svg>
              </div>
              <input
                name="customerName"
                value={formData.clientDetails?.customerName || ""}
                onChange={handleInputChange}
                placeholder="Enter customer name"
                className="flex-1 bg-transparent border-none outline-none text-gray-700 font-medium placeholder-gray-400 text-base"
              />
            </div>
            <div className="h-0.5 bg-gradient-to-r from-gray-400 to-gray-300 rounded-full"></div>
          </div>
        </InputWrapper>

        {/* Address */}
        <InputWrapper
          label="Address"
          required
          error={errors.address}
        >
          <div className="relative">
            <div className="flex items-center gap-3 pb-3">
              <div className="w-5 h-5 text-blue-500 flex-shrink-0">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path fillRule="evenodd" d="M11.54 22.351l.07.04.028.016a.76.76 0 00.723 0l.028-.015.071-.041a16.975 16.975 0 001.144-.742 19.58 19.58 0 002.683-2.282c1.944-1.99 3.963-4.98 3.963-8.827a8.25 8.25 0 00-16.5 0c0 3.846 2.02 6.837 3.963 8.827a19.58 19.58 0 002.682 2.282 16.975 16.975 0 001.145.742zM12 13.5a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                </svg>
              </div>
              <input
                name="address"
                value={formData.clientDetails?.address || ""}
                onChange={handleInputChange}
                placeholder="Enter address"
                className="flex-1 bg-transparent border-none outline-none text-gray-700 font-medium placeholder-gray-400 text-base"
              />
            </div>
            <div className="h-0.5 bg-gradient-to-r from-gray-400 to-gray-300 rounded-full"></div>
          </div>
        </InputWrapper>

        {/* City */}
        <InputWrapper label="City" required error={errors.city}>
          <div className="relative">
            <div className="flex items-center gap-3 pb-3">
              <div className="w-5 h-5 text-blue-500 flex-shrink-0">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3 21V3h4v18H3zm6 0V9h4v12H9zm6 0V15h4v6h-4z"/>
                </svg>
              </div>
              <input
                name="city"
                value={formData.clientDetails?.city || ""}
                onChange={handleInputChange}
                placeholder="Enter city"
                className="flex-1 bg-transparent border-none outline-none text-gray-700 font-medium placeholder-gray-400 text-base"
              />
            </div>
            <div className="h-0.5 bg-gradient-to-r from-gray-400 to-gray-300 rounded-full"></div>
          </div>
        </InputWrapper>

        {/* State */}
        <InputWrapper label="State" required error={errors.state}>
          <div className="relative">
            <div className="flex items-center gap-3 pb-3">
              <div className="w-5 h-5 text-blue-500 flex-shrink-0">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                </svg>
              </div>
              <input
                name="state"
                value={formData.clientDetails?.state || ""}
                onChange={handleInputChange}
                placeholder="Enter state"
                className="flex-1 bg-transparent border-none outline-none text-gray-700 font-medium placeholder-gray-400 text-base"
              />
            </div>
            <div className="h-0.5 bg-gradient-to-r from-gray-400 to-gray-300 rounded-full"></div>
          </div>
        </InputWrapper>
      </form>

      {/* Buttons */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 flex gap-3 md:relative md:border-t-0 md:mt-8 md:gap-4">
        <button
          onClick={handleReset}
          className="flex-1 px-6 py-3 border-2 border-gray-400 text-gray-700 rounded-lg hover:bg-gray-100 transition font-semibold text-sm md:text-base"
        >
          Reset
        </button>
        <button
          onClick={handleNext}
          className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-semibold text-sm md:text-base shadow-md"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default ClientDetailsStep;
